import { Save, Send } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  TextField,
  Typography
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import React from 'react'
import { connect } from 'react-redux'
import { NivosModal } from 'components/index'
import NivosRTE from 'components/nivos-rte'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as NewsOperations,
  operations as newsOperations
} from 'states/dux/nivos-news/operations'
import { Payload as NewsPayload } from 'states/dux/nivos-news/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserAdminOperations,
  operations as userAdminOperations
} from 'states/dux/user-admin/operations'
import { Payload as UserAdminPayload } from 'states/dux/user-admin/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosNews } from 'types/nivos-news'
import { NivosUser, NivosUserGroup } from 'types/user'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  news: NewsPayload
  userAdmin: UserAdminPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    news: NewsOperations
    userAdmin: UserAdminOperations
  }
}

interface OwnProps {
  selectedNews: NivosNews | undefined
  setSelectedNews: (isOpen: NivosNews | undefined) => void
}

interface SelectFieldOptions {
  label: string
  value: number
}

type AppProps = StateProps & DispatchProps & OwnProps

const NewsAdminModalFC: React.FC<any> = (props: AppProps) => {
  const { user, operations, selectedNews, setSelectedNews } = props

  const [groupSelection, setGroupSelection] = React.useState<
  SelectFieldOptions[]
  >([])

  const [userSelection, setUserSelection] = React.useState<
  SelectFieldOptions[]
  >([])

  const [selectedGroupSelection, setSelectedGroupSelection] = React.useState<
  SelectFieldOptions[]
  >([])

  const [selectedUserSelection, setSelectedUserSelection] = React.useState<
  SelectFieldOptions[]
  >([])

  const [title, setTitle] = React.useState('')
  const [scheduledNews, setScheduledNews] = React.useState(false)
  const [newsDate, setNewsDate] = React.useState<Date | null>(null)
  const [newsImportant, setNewsImportant] = React.useState(true)
  const [newsActive, setNewsActive] = React.useState(true)
  const [newsArchived, setNewsArchived] = React.useState(false)
  const [newsImages, setImages] = React.useState<File>()
  const [rteMessage, setRteMessage] = React.useState('')
  const [message, setMessage] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [newsUpdate, setNewsUpdate] = React.useState(
    selectedNews !== undefined && selectedNews.id !== 0
  )

  const defaultErrors = {
    title: false,
    receiverGroups: false,
    receiverUsers: false,
    scheduledNews: false,
    active: false,
    date: false,
    images: false,
    message: false,
    rteMessage: false
  }

  const [errors, setErrors] = React.useState(defaultErrors)

  const _generateFormData = (): FormData => {
    const formData = new FormData()
    if (newsImages !== undefined) {
      formData.append('image', newsImages, newsImages.name)
    }
    formData.append('created_by', user.id.toString())
    if (selectedGroupSelection.length > 0) {
      formData.append(
        'groups_id',
        selectedGroupSelection.map(item => item.value).toString()
      )
    }
    if (selectedUserSelection.length > 0) {
      formData.append(
        'users_id',
        selectedUserSelection.map(item => item.value).toString()
      )
    }
    formData.append('title', title)
    formData.append('message', message)
    formData.append('rte_message', rteMessage)
    if (newsDate !== null && scheduledNews) {
      formData.append('scheduled_at', newsDate.toISOString())
    } else if (!scheduledNews) {
      formData.append('scheduled_at', '')
    }

    formData.append('active', newsActive.toString())
    formData.append('archived', newsArchived.toString())
    formData.append('important', newsImportant.toString())

    return formData
  }

  const _validateInputs = (): void => {
    setLoading(true)
    const requiredFields = {
      title,
      selectedGroupSelection,
      message
    }

    let errorStates = errors
    let hasErrors = false
    Object.entries(requiredFields).forEach(entry => {
      if (
        entry[1] === '' ||
        entry[1] === null ||
        entry[1] === undefined ||
        (Array.isArray(entry[1]) && entry[1].length === 0)
      ) {
        errorStates = { ...errorStates, [entry[0]]: true }
        hasErrors = true
      }
    })
    setErrors(errorStates)
    if (hasErrors) {
      setLoading(false)
      return
    }
    if (selectedNews !== undefined && selectedNews.id === 0) {
      void post('/notification/', _generateFormData(), true)
        .then(result => {
          try {
            operations.news.add(objectifyDateStringFields(result))
            setSelectedNews(undefined)
          } catch (e) {
            console.log(e)
          }
        })
        .finally(() => setLoading(false))
    } else if (selectedNews !== undefined && selectedNews.id > 0) {
      void post(
        `/notification/${selectedNews.id}/`,
        _generateFormData(),
        true,
        false,
        'PUT'
      )
        .then(result => {
          try {
            operations.news.update(objectifyDateStringFields(result))
            setSelectedNews(undefined)
          } catch (e) {
            console.log(e)
          }
        })
        .finally(() => setLoading(false))
    }
  }

  React.useEffect(() => {
    if (userSelection.length === 0) {
      void get('/account/').then((data: NivosUser[]) => {
        try {
          setUserSelection(
            data.map(item => ({ label: item.fullName, value: item.id }))
          )
        } catch (e) {
          console.log(e)
        }
      })
    }
    if (groupSelection.length === 0) {
      void get('/account/group/').then((data: NivosUserGroup[]) => {
        try {
          setGroupSelection(
            data.map(item => ({ label: item.name, value: item.id }))
          )
        } catch (e) {
          console.log(e)
        }
      })
    }
  }, [])

  React.useEffect(() => {
    if (selectedNews !== undefined && selectedNews.id > 0) {
      // Fill newsData
      setSelectedGroupSelection(
        selectedNews.notifyGroup.map(item => {
          return { label: item.name, value: item.id }
        })
      )

      setSelectedUserSelection(
        selectedNews.notifyUsers.map(item => {
          return { label: item.fullName, value: item.id }
        })
      )

      setTitle(selectedNews.title)
      setScheduledNews(selectedNews.scheduledAt instanceof Date)
      setNewsDate(
        selectedNews.scheduledAt instanceof Date
          ? selectedNews.scheduledAt
          : null
      )
      setNewsImportant(selectedNews.important)
      setNewsActive(selectedNews.active)
      setNewsArchived(selectedNews.archived)
      setNewsUpdate(true)
      setRteMessage(selectedNews.rteMessage)
      setMessage(selectedNews.message)
    } else {
      setTitle('')
      setRteMessage('')
      setNewsDate(null)
      setSelectedGroupSelection([])
      setSelectedUserSelection([])
      setNewsUpdate(false)
      setRteMessage('')
      setMessage('')
    }
    setErrors(defaultErrors)
  }, [selectedNews])

  return (
    <NivosModal
      title={(selectedNews?.id === 0 ? 'PUBLISH' : 'EDIT') + ' NEWS'}
      open={selectedNews !== undefined}
      setOpen={_ => setSelectedNews(undefined)}
    >
      <Box display={'flex'} width={900}>
        <Box paddingRight={1} minWidth={'207px'}>
          <Typography variant={'h5'} paddingY={2} paddingBottom={3}>
            News Title:
          </Typography>
          <Typography variant={'h5'} paddingY={2} paddingBottom={3} noWrap>
            Send to Groups:
          </Typography>
          <Typography variant={'h5'} paddingY={2} paddingBottom={3}>
            Send to Users:
          </Typography>
          <Typography variant={'h5'} paddingY={2} paddingBottom={3}>
            Description:
          </Typography>
        </Box>
        <Box flexGrow={1}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            minWidth={500}
            height={1}
          >
            <Box paddingY={1} width={1}>
              <TextField
                placeholder={'News Title'}
                error={errors.title}
                value={title}
                onChange={event => {
                  setErrors({ ...errors, title: false })
                  setTitle(event.currentTarget.value)
                }}
                fullWidth
              />
            </Box>
            <Box paddingY={1} width={1}>
              <Autocomplete
                disablePortal
                multiple
                options={groupSelection}
                value={selectedGroupSelection}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(event, value) => {
                  setSelectedGroupSelection(value)
                  setErrors({ ...errors, receiverGroups: false })
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={errors.receiverGroups}
                    label="Receiver ( Groups )"
                  />
                )}
              />
            </Box>
            <Box paddingY={1} width={1}>
              <Autocomplete
                disablePortal
                multiple
                options={userSelection}
                value={selectedUserSelection}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(event, value) => {
                  setSelectedUserSelection(value)
                  setErrors({ ...errors, receiverUsers: false })
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={errors.receiverUsers}
                    label="Receiver ( Users )"
                  />
                )}
              />
            </Box>
            <Box paddingY={1} width={1}>
              <NivosRTE
                error={errors.message}
                rteState={
                  selectedNews !== undefined && selectedNews.id > 0
                    ? selectedNews.rteMessage
                    : rteMessage
                }
                setRteState={setRteMessage}
                setPLainState={plainMessage => {
                  setErrors({ ...errors, message: false })
                  setMessage(plainMessage)
                }}
              />
            </Box>
            <Box
              paddingTop={1}
              minHeight={72}
              width={1}
              display={'flex'}
              alignItems={'center'}
            >
              <Typography paddingX={1}>Important</Typography>
              <Checkbox
                checked={newsImportant}
                onChange={(_, checked) => setNewsImportant(checked)}
              />
            </Box>
            <Box
              minHeight={72}
              width={1}
              display={'flex'}
              alignItems={'center'}
            >
              <Typography paddingX={1}>
                {newsUpdate ? 'Active' : 'Activate on create'}
              </Typography>
              <Checkbox
                checked={newsActive}
                onChange={(_, checked) => {
                  setNewsActive(checked)
                  if (checked) {
                    setNewsArchived(false)
                    setScheduledNews(false)
                    setNewsDate(null)
                  } else {
                    setNewsArchived(true)
                  }
                }}
              />
            </Box>
            <Box
              minHeight={72}
              width={1}
              display={'flex'}
              alignItems={'center'}
            >
              <Typography paddingX={1}>Archived News</Typography>
              <Checkbox
                checked={newsArchived}
                onChange={(_, checked) => {
                  setNewsArchived(checked)
                  if (checked) {
                    setNewsActive(false)
                    setScheduledNews(false)
                    setNewsDate(null)
                  } else {
                    setNewsActive(true)
                  }
                }}
              />
            </Box>
            <Box
              paddingTop={1}
              minHeight={72}
              width={1}
              display={'flex'}
              flexDirection={'column'}
            >
              <Box display={'flex'} alignItems={'center'}>
                <Typography paddingX={1}>Scheduled News?</Typography>
                <Checkbox
                  checked={scheduledNews}
                  onChange={(_, checked) => {
                    setScheduledNews(checked)
                    if (checked) {
                      setNewsArchived(false)
                      setNewsActive(false)
                    } else {
                      setNewsActive(true)
                    }
                  }}
                />
              </Box>
              <Box flexGrow={1} />
              {scheduledNews && (
                <Box display={'flex'} alignItems={'center'}>
                  <Typography paddingX={1}>Will be published at:</Typography>
                  <DateTimePicker
                    disablePast
                    value={newsDate}
                    onChange={date => {
                      setNewsDate(date)
                    }}
                    renderInput={_props => (
                      <TextField {..._props} sx={{ width: 240 }} />
                    )}
                  />
                </Box>
              )}
            </Box>
            {!newsUpdate && (
              <>
                <Box
                  paddingY={1}
                  width={1}
                  display={'flex'}
                  alignItems={'center'}
                  borderRadius={4}
                  sx={{ border: errors.images ? '1px solid red' : 'none' }}
                >
                  <Typography paddingX={1}>News image:</Typography>
                  <input
                    type={'file'}
                    title={'Cover image'}
                    accept={'image/*'}
                    onChange={event => {
                      setErrors({ ...errors, images: false })
                      if (event.currentTarget.files != null) {
                        setImages(event.currentTarget.files[0])
                      }
                    }}
                  />
                </Box>
                <Box paddingY={2} />
              </>
            )}
            <Box
              paddingY={1}
              width={1}
              display={'flex'}
              justifyContent={'flex-end'}
              alignItems={'center'}
            >
              <Button
                variant={'text'}
                endIcon={newsUpdate ? <Save /> : <Send />}
                onClick={() => _validateInputs()}
                disabled={loading}
              >
                {newsUpdate ? 'SAVE' : 'PUBLISH'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </NivosModal>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    news: state.news,
    userAdmin: state.userAdmin
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  news: newsOperations,
  userAdmin: userAdminOperations
})

const NewsAdminModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(NewsAdminModalFC) as React.FC<OwnProps>

export default NewsAdminModal
